/*
 * product-slider-nav.js
 * Adds prev/next arrows overlaid on the main product photo and keyboard
 * (Left/Right) navigation to the product media gallery, on all screen sizes.
 *
 * The theme's gallery is a Vue + Swiper widget (in the minified theme.js) that
 * we must not touch. Instead of driving Swiper directly, we reuse the theme's
 * own media-activation path: clicking the matching thumbnail (or, if there are
 * no thumbnails, the matching pagination bullet) is exactly what changes the
 * main image, and it keeps variant state / zoom / active states in sync on
 * every breakpoint. We detect the currently-shown image from the visible slide.
 *
 * All behaviour is wired with EVENT DELEGATION on `document`, so it keeps
 * working even when Vue/Swiper rebuild (or clone) the gallery DOM after we have
 * injected the arrow buttons — per-element listeners would be lost in that case.
 */
(function () {
  'use strict';

  var PREV_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15.5 4.5 8 12l7.5 7.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var NEXT_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8.5 4.5 16 12l-7.5 7.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function slides(main) {
    return Array.prototype.slice.call(
      main.querySelectorAll('.swiper-wrapper > .swiper-slide')
    );
  }

  // Ordered list of the controls that switch the main image. Thumbnails first
  // (present in the product-page layout on every breakpoint); fall back to the
  // swiper pagination bullets for galleries with no thumbnails (e.g. full width).
  function controls(container) {
    var thumbs = container.querySelectorAll(
      '.product-medias__thumbnails .product-medias__thumbnail'
    );
    if (thumbs.length) return Array.prototype.slice.call(thumbs);
    var bullets = container.querySelectorAll(
      '.swiper-pagination .swiper-pagination-bullet'
    );
    return Array.prototype.slice.call(bullets);
  }

  // Ground-truth current slide: the one whose horizontal center sits closest to
  // the swiper viewport center. Works for desktop (one slide shown) and mobile
  // (swipeable), regardless of any stale swiper-slide-active class.
  function currentIndex(main) {
    var sl = slides(main);
    if (!sl.length) return 0;
    var viewport = main.querySelector('.swiper-container') || main;
    var vr = viewport.getBoundingClientRect();
    var center = vr.left + vr.width / 2;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < sl.length; i++) {
      var r = sl[i].getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      var d = Math.abs(r.left + r.width / 2 - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  // The live Swiper instance backing a gallery (Swiper stores itself on the
  // container element). This is the reliable way to move the visible image on
  // every breakpoint — on mobile, clicking thumbnails/bullets does NOT scroll
  // the swiper, but slideTo() does.
  function swiperOf(container) {
    var el = container.querySelector('.product-medias__main .swiper-container');
    return el && el.swiper ? el.swiper : null;
  }

  // Move the gallery that `fromEl` lives in by `dir` (+1 next / -1 prev).
  // Live-resolves everything at call time so re-renders can't leave it stale.
  function go(fromEl, dir) {
    var container = fromEl && fromEl.closest('.product-images-container');
    if (!container) return;

    // Preferred path: drive the Swiper instance directly (works on all sizes).
    var sw = swiperOf(container);
    if (sw && typeof sw.slideTo === 'function') {
      var total = sw.slides ? sw.slides.length : 0;
      if (total < 2) return;
      var at = typeof sw.realIndex === 'number' ? sw.realIndex : sw.activeIndex;
      sw.slideTo((at + dir + total) % total); // wrap around
      return;
    }

    // Fallback: reuse the theme's media-activation path via the thumbnails
    // (or pagination bullets) when the Swiper instance can't be reached.
    var main = container.querySelector('.product-medias__main');
    if (!main) return;
    var ctrls = controls(container);
    var sl = slides(main);
    var count = Math.min(ctrls.length, sl.length) || ctrls.length;
    if (count < 2) return;
    var cur = currentIndex(main);
    var next = (cur + dir + count) % count; // wrap around
    if (ctrls[next]) ctrls[next].click();
  }

  function makeArrow(kind, label) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'product-medias__nav-arrow product-medias__nav-arrow--' + kind;
    btn.setAttribute('aria-label', label);
    btn.setAttribute('data-nav-dir', kind === 'prev' ? '-1' : '1');
    btn.innerHTML = kind === 'prev' ? PREV_SVG : NEXT_SVG;
    return btn;
  }

  // Inject the two arrow buttons into a gallery (idempotent). No per-button
  // listeners — clicks are handled by the delegated document listener below.
  function injectArrows(container) {
    var main = container.querySelector('.product-medias__main');
    if (!main) return;
    var host = main.querySelector('.swiper-container') || main;

    // Already have live arrows in this host? Nothing to do.
    if (host.querySelector(':scope > .product-medias__nav-arrow')) return;

    // Only add arrows when there is more than one media to move between.
    if (slides(main).length < 2 && controls(container).length < 2) return;

    if (getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }

    host.appendChild(makeArrow('prev', 'Previous image'));
    host.appendChild(makeArrow('next', 'Next image'));
  }

  function injectAll() {
    var containers = document.querySelectorAll('.product-images-container');
    for (var i = 0; i < containers.length; i++) injectArrows(containers[i]);
  }

  function bindGlobalListeners() {
    // Delegated click: survives Vue/Swiper rebuilding the arrow DOM.
    document.addEventListener(
      'click',
      function (e) {
        var arrow = e.target && e.target.closest
          ? e.target.closest('.product-medias__nav-arrow')
          : null;
        if (!arrow) return;
        e.preventDefault();
        e.stopPropagation();
        go(arrow, parseInt(arrow.getAttribute('data-nav-dir'), 10) || 1);
      },
      true // capture, so we run before Swiper's own click handling
    );

    // Keyboard: Left/Right move the gallery the user is currently engaged with
    // (pointer over it, or focus inside it). Scoped so we never hijack arrow
    // keys for normal page scrolling.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var t = e.target;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      ) {
        return;
      }
      var container = engagedGallery();
      if (!container) return;
      e.preventDefault();
      go(container, e.key === 'ArrowRight' ? 1 : -1);
    });
  }

  // The product gallery the user is interacting with: hovered, or containing
  // the focused element.
  function engagedGallery() {
    var galleries = document.querySelectorAll('.product-images-container');
    for (var i = 0; i < galleries.length; i++) {
      var g = galleries[i];
      if (g.matches(':hover') || g.contains(document.activeElement)) return g;
    }
    return null;
  }

  function init() {
    bindGlobalListeners();
    injectAll();
    // Re-inject after the Vue gallery finishes mounting / on variant re-render.
    setTimeout(injectAll, 400);
    setTimeout(injectAll, 1200);

    if (document.body && 'MutationObserver' in window) {
      var scheduled = false;
      var mo = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(function () {
          scheduled = false;
          injectAll();
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
